import { ReactNode, useState } from "react";
import {
  TableBody,
  TableCell,
  Table as TableComponent,
  TableHead,
  TableRow,
} from "@/view/components/ui/table";
import { cn } from "@/app/utils/cn";
import {
  Commission,
  CondicaoCredito,
} from "@/app/services/panService/generateOffers";
import { addMoneySimbols } from "@/app/utils/formatMoneyValue";

interface GrayCardProps {
  title: string;
  headers: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows: any[][];
  children?: ReactNode;
}

const GrayCard = ({ title, headers, rows, children }: GrayCardProps) => {
  return (
    <div className="overflow-x-auto rounded-lg bg-white02 p-3">
      <p className="pb-3 font-semibold">{title}</p>
      <TableComponent>
        <TableRow className="border-y font-medium">
          {headers.map((header, i) => (
            <TableHead
              className="text-center text-black01 first:text-left first:font-semibold last:text-right"
              key={`${header}-${i}`}
            >
              {header}
            </TableHead>
          ))}
        </TableRow>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row[0]}>
              {row.map((cell, i) => (
                <TableCell
                  key={`${cell}-${i}`}
                  className="text-center first:text-left first:font-semibold first:text-black01 last:text-right"
                >
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {children && (
            <TableRow>
              <TableCell colSpan={headers.length} className="text-center">
                {children}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </TableComponent>
    </div>
  );
};

interface ItemProps {
  title: string;
  value: string | ReactNode;
}

const Item = ({ title, value }: ItemProps) => {
  return (
    <div className="flex justify-between gap-2">
      <p className="text-sm font-medium text-black04">{title}</p>
      <span className="text-sm font-medium text-black01">{value}</span>
    </div>
  );
};

interface OfferCardProps {
  offer: CondicaoCredito;
  isSelected?: boolean;
  selectedValue?: number;
  commission?: Commission;
  onClick?: () => void;
}

export function OfferCard({
  offer,
  isSelected = false,
  selectedValue,
  commission,
  onClick,
}: OfferCardProps) {
  if (!offer.sucesso) return null;

  const seguro = offer.despesas?.find((d) => d.grupo === "SEGURO");
  const [showAllParcels, setShowAllParcels] = useState(false);
  const displayedParcels = showAllParcels
    ? offer.parcelas
    : offer.parcelas.slice(0, 5);

  const formatMonthAndYear = (data: string) => {
    if (!data) return "";
    const [mes, ano] = data.split("-");
    return `${mes}/${ano}`;
  };

  return (
    <div
      className={cn(
        "flex max-w-fit flex-col gap-4 rounded-lg border p-4 sm:max-w-max",
        isSelected && "border-primary shadow-md shadow-primary",
      )}
      onClick={onClick}
    >
      <div className="rounded-lg border border-primary bg-primary/10 p-2.5">
        <div className="flex justify-between gap-5 border-b pb-2.5">
          <div>
            <p className="text-xs font-medium text-black04">Valor líquido</p>
            <span className="font-semibold">
              {addMoneySimbols(offer.valor_liquido)}
            </span>
          </div>
          <div className="w-[1px] bg-border" />
          <div className="flex flex-col items-center">
            <p className="text-xs font-medium text-black04">
              Comissão ({commission?.percents.toFixed(2)}%)
            </p>
            <span className="font-medium">
              {addMoneySimbols(
                ((commission?.percents ?? 0) * offer.valor_liquido) / 100,
              )}
            </span>
          </div>
          <div className="w-[1px] bg-border" />
          <div className="flex flex-col items-end">
            <p className="text-xs font-medium text-black04">Parcelas</p>
            <span>{offer.prazo}</span>
          </div>
        </div>
        <div className="flex justify-between pt-2.5">
          <div>
            <p className="text-xs font-medium text-black04">
              Primeiro vencimento
            </p>
            <span className="text-sm font-medium">
              {formatMonthAndYear(offer.primeiro_vencimento || "")}
            </span>
          </div>
          <div className="flex flex-col items-end">
            <p className="text-xs font-medium text-black04">
              Último vencimento
            </p>
            <span className="text-sm font-medium">
              {formatMonthAndYear(offer.ultimo_vencimento || "")}
            </span>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2.5 px-1">
        <Item
          title="Seguro"
          value={addMoneySimbols(seguro?.valor_calculado ?? 0)}
        />
        <Item title="IOF" value={addMoneySimbols(offer.valor_iof)} />
      </div>

      <GrayCard
        title="Parcelas"
        headers={["N°", "Vencimento", "Valor"]}
        rows={displayedParcels.map((vencimento, index) => [
          index + 1,
          vencimento,
          addMoneySimbols(selectedValue || 0),
        ])}
      >
        {offer.parcelas.length > 5 && (
          <button
            className="text-primary"
            onClick={() => setShowAllParcels(!showAllParcels)}
          >
            {showAllParcels ? "Ver menos" : "Ver mais"}
          </button>
        )}
      </GrayCard>

      <GrayCard
        title="Taxas"
        headers={["Nome", "Mensal (AM)", "Anual (AA)"]}
        rows={[
          [
            "Taxa CET",
            `${offer.taxa_cet_mensal.toFixed(2)}%`,
            `${offer.taxa_cet_anual.toFixed(2)}%`,
          ],
          [
            "Taxa Jr.",
            `${offer.taxa_apropriacao_mensal.toFixed(2)}%`,
            `${offer.taxa_apropriacao_anual.toFixed(2)}%`,
          ],
        ]}
      />
    </div>
  );
}
